// The text-mode utterance editor (transcripts.md module plan): table | JSON
// input toggle, Add utterance, and the draft-row DataTable — extracted from
// the 934-line TranscriptUploadForm where it lived as an unexported function.
// Props-pure; all draft/parse rules live in lib/utteranceDraft.

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import {
  utteranceRoles,
  type TextUtteranceInputMode,
  type TranscriptUtteranceDraft,
} from "../../lib/utteranceDraft";
import {
  Button,
  DataTable,
  EmptyState,
  Inline,
  Section,
  Select,
  Textarea,
  TextInput,
} from "../../ui";

type UtteranceRole = components["schemas"]["UtteranceRole"];

export interface TextUtteranceEditorProps {
  busy: boolean;
  inputMode: TextUtteranceInputMode;
  jsonError: string | null;
  jsonValue: string;
  onAddRow: () => void;
  onInputModeChange: (mode: TextUtteranceInputMode) => void;
  onJsonChange: (value: string) => void;
  onRemoveRow: (id: string) => void;
  onRowChange: (
    id: string,
    patch: Partial<Omit<TranscriptUtteranceDraft, "id">>,
  ) => void;
  rows: TranscriptUtteranceDraft[];
}

export function TextUtteranceEditor({
  busy,
  inputMode,
  jsonError,
  jsonValue,
  onAddRow,
  onInputModeChange,
  onJsonChange,
  onRemoveRow,
  onRowChange,
  rows,
}: TextUtteranceEditorProps) {
  const columns: Array<ColumnDef<TranscriptUtteranceDraft>> = [
    {
      header: "Role",
      cell: ({ row }) => (
        <Select
          disabled={busy}
          label="Role"
          value={row.original.role}
          onChange={(event) => {
            onRowChange(row.original.id, {
              role: event.currentTarget.value as UtteranceRole,
            });
          }}
        >
          {utteranceRoles.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </Select>
      ),
    },
    {
      header: "Text",
      cell: ({ row }) => (
        <Textarea
          disabled={busy}
          label="Text"
          value={row.original.text}
          onChange={(event) => {
            onRowChange(row.original.id, {
              text: event.currentTarget.value,
            });
          }}
        />
      ),
    },
    {
      header: "Start ms",
      cell: ({ row }) => (
        <TextInput
          disabled={busy}
          label="Start ms"
          min={0}
          step={1}
          type="number"
          value={row.original.startMs}
          onChange={(event) => {
            onRowChange(row.original.id, {
              startMs: event.currentTarget.value,
            });
          }}
        />
      ),
    },
    {
      header: "End ms",
      cell: ({ row }) => (
        <TextInput
          disabled={busy}
          label="End ms"
          min={0}
          step={1}
          type="number"
          value={row.original.endMs}
          onChange={(event) => {
            onRowChange(row.original.id, {
              endMs: event.currentTarget.value,
            });
          }}
        />
      ),
    },
    {
      header: "Action",
      cell: ({ row }) => (
        <Button
          disabled={busy}
          size="sm"
          variant="secondary"
          onClick={() => {
            onRemoveRow(row.original.id);
          }}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Section heading="Utterances">
      <Inline gap={2}>
        <Button
          aria-pressed={inputMode === "table"}
          disabled={busy}
          size="sm"
          variant={inputMode === "table" ? "primary" : "secondary"}
          onClick={() => {
            onInputModeChange("table");
          }}
        >
          Table
        </Button>
        <Button
          aria-pressed={inputMode === "json"}
          disabled={busy}
          size="sm"
          variant={inputMode === "json" ? "primary" : "secondary"}
          onClick={() => {
            onInputModeChange("json");
          }}
        >
          JSON
        </Button>
        {inputMode === "table" ? (
          <Button
            disabled={busy}
            size="sm"
            variant="secondary"
            onClick={onAddRow}
          >
            Add utterance
          </Button>
        ) : null}
      </Inline>
      {inputMode === "json" ? (
        <Textarea
          disabled={busy}
          error={jsonError}
          label="Utterances JSON"
          rows={12}
          value={jsonValue}
          onChange={(event) => {
            onJsonChange(event.currentTarget.value);
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          description="Add at least one utterance before submitting text."
          title="No utterances"
        />
      ) : (
        <DataTable
          ariaLabel="Utterances"
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
        />
      )}
    </Section>
  );
}
